/**
 * The seam every part reads. One context, one provider, one session.
 *
 * Nothing on this value is a prop on a part, and that is the point of the whole
 * arrangement: a part positions or labels, it never carries the session's state
 * around for it. `geometry` above all — the variants are chrome, so they read
 * where the surface is rather than being told, and there is no geometry prop
 * for a caller to disagree with.
 */
import { type CSSProperties, createContext, type PointerEvent, type RefObject, use } from "react";

import type { CallParticipant } from "./participant-roster";

/**
 * Where the connection is in its life. Device acquisition failures travel
 * separately in `deviceErrors`: a denied camera must not fail a live call.
 */
export type CallStatus = "connecting" | "live" | "ended";

/** One vendor video tile, in neutral terms. */
export interface CallTile {
  tileId: number;
  attendeeId: string;
  name: string;
  /** A screen share rather than a camera — letterboxed, never cropped. */
  isContent: boolean;
  isLocal: boolean;
}

/**
 * Somebody at the door. Supplied by the caller: this package has no idea who
 * they are, where the list came from, or what admitting one means.
 */
export interface WaitingEntry {
  id: string;
  displayName: string;
  /** Epoch ms they knocked, for the elapsed counter. */
  since: number;
}

/**
 * Where the surface is and how it is shaped, derived from the store's
 * `minimised` and the anchor element. Both layers — the stage the provider owns
 * and the chrome a variant renders — read the same value, which is what keeps
 * them aligned without either one measuring anything.
 */
export interface CallGeometry {
  minimised: boolean;
  /** Inline position for a fixed layer: the stage AND the column beside it. */
  style: CSSProperties;
  /** The stage alone, inset by the waiting room column when it is open. */
  stageStyle: CSSProperties;
  /** The waiting room column's own layer, at the region's right edge. */
  asideStyle: CSSProperties;
  /** True only when this surface has a waiting room and it is slid open. */
  asideOpen: boolean;
  /** True while the minimised card is being dragged. */
  dragging: boolean;
  /** Wire onto the frame: the minimised card drags, the expanded one does not. */
  onPointerDown: (event: PointerEvent<HTMLElement>) => void;
}

export interface CallActions {
  toggleMute: () => void;
  toggleCamera: () => void;
  toggleShare: () => void;
  /** Leave and tell the caller. */
  leave: () => void;
  minimise: () => void;
  expand: () => void;
  /** Slide the waiting room column open or shut. */
  toggleAside: () => void;
  /** Let one person in. The caller decides what that means. */
  admit: (id: string) => void;
  decline: (id: string) => void;
}

export interface CallContextValue {
  status: CallStatus;
  /** Device failure is recoverable and does not change connection status. */
  deviceErrors: { camera?: string; microphone?: string };
  previewReady: boolean;
  bindPreview: (element: HTMLVideoElement | null) => void;
  tiles: readonly CallTile[];
  /** Live attendance, independent of whether anyone is sending video. */
  roster: ReadonlyMap<string, CallParticipant>;
  /** Latched for this room: camera-off and departure must not restore ringing chrome. */
  hasRemoteAttendeeJoined: boolean;
  /** Server-attributed: the only honest signal that the vendor has someone's audio. */
  speaking: ReadonlySet<string>;
  muted: boolean;
  cameraOn: boolean;
  sharing: boolean;
  canShareScreen: boolean;
  title: string | undefined;
  waiting: readonly WaitingEntry[];
  /**
   * Whether this surface HAS a door at all, which is not the same question as
   * whether anyone is at it. `waiting` is normalised to an empty array so that
   * every reader can iterate it, and that normalisation is exactly what made
   * "is there a waiting room" unanswerable — a guest's surface was showing a
   * toggle for a panel that does not exist over there.
   */
  hasWaitingRoom: boolean;
  /** The entry whose admission is in flight, so its control can say so. */
  admitting: string | null;
  geometry: CallGeometry;
  actions: CallActions;
  /**
   * Binds one vendor tile to one `<video>`, as a ref callback — binding needs
   * the element, so it cannot happen before the ref fires. The stage is its
   * only legitimate caller.
   */
  bindTile: (tileId: number) => (element: HTMLVideoElement | null) => void;
  /**
   * THE ELEMENT REMOTE AUDIO IS BOUND TO. It is bound once, and the element it
   * points at must never be replaced — see the stage.
   */
  audioRef: RefObject<HTMLAudioElement | null>;
}

export const CallContext = createContext<CallContextValue | null>(null);

export function useCall(): CallContextValue {
  const ctx = use(CallContext);
  if (!ctx) throw new Error("Call.* components must be rendered inside <ConferencingProvider>");
  return ctx;
}
